import { Data, Layout } from "plotly.js";
import { useCallback, useMemo } from "react";
import Plot from "react-plotly.js";
import { BACKGROUND_COLORS } from "../../constants/design/colors";
import { useOptionsTrades } from "../../hooks/api/mm/useOptionsTrades";
import { useOptionsMarkHistory } from "../../hooks/api/mm/useOptionsMarkHistory";
import { ChartContainer } from "./style";

export interface IInstrumentChartProps {
  instrumentName: string;
  startTime: number;
  endTime: number;
}

function InstrumentChart({ instrumentName, startTime, endTime }: IInstrumentChartProps) {
  const legendMarkerSize = 15;
  const minMarkerSize = 10; // Minimum marker size
  const maxMarkerSize = 15; // Maximum marker size
  const instrument_name = instrumentName;
  const secondaryExchange = "DERIBIT";

  const { data: markHistory } = useOptionsMarkHistory(String(startTime), String(endTime), instrument_name);

  const { data: aevoTrades } = useOptionsTrades(String(startTime), String(endTime), instrument_name, "AEVO");

  const { data: secondaryExchangeTrades } = useOptionsTrades(String(startTime), String(endTime), instrument_name, "DERIBIT");

  const normalizeSize = useCallback(
    (amount: number, minAmount: number, maxAmount: number) => {
      if (maxAmount === minAmount) {
        // Handle the case where maxAmount and minAmount are the same
        return minMarkerSize; // or any default size you prefer
      }
      return ((amount - minAmount) / (maxAmount - minAmount)) *
        (maxMarkerSize - minMarkerSize) +
        minMarkerSize;
    },
    []
  );

  const layout = useMemo(
    () => ({
        title: `${instrumentName}`,
        xaxis: {
          autorange: true,
          type: "date",
        },
        yaxis: {
          autorange: true,
        },
        plot_bgcolor: BACKGROUND_COLORS.five,
        paper_bgcolor: "transparent", // Set overall background color to black
        font: {
          color: "white", // Optional: Change font color for better contrast
        },
      } as Layout),
    [instrumentName]
  );

  const plotData = useMemo(() => {
    if (!aevoTrades || !secondaryExchangeTrades || !markHistory) {
      return [] as Data[];
    }
    const allTrades = [...aevoTrades, ...secondaryExchangeTrades];
    const maxAmount = Math.max(
      ...allTrades.map((trade) => parseFloat(trade.Amount))
    );
    const minAmount = Math.min(
      ...allTrades.map((trade) => parseFloat(trade.Amount))
    );

    const aevoBuys = aevoTrades.filter((trade) => trade.Side === "buy");
    const aevoSells = aevoTrades.filter((trade) => trade.Side === "sell");

    const secondaryExchangeBuys = secondaryExchangeTrades.filter(
      (trade) => trade.Side === "buy"
    );
    const secondaryExchangeSells = secondaryExchangeTrades.filter(
      (trade) => trade.Side === "sell"
    );

    return [
      {
        x: markHistory.map((item) => new Date(parseInt(item.timestamp, 10)).toISOString()),
        y: markHistory.map((item) => parseFloat(item.price)),
        type: "scatter",
        mode: "lines",
        line: {
          color: "white",
          width: 0.5, // Adjust this value to make the line thinner
        },
        name: "Price",
      },
      {
        x: aevoBuys.map((trade) => new Date(parseInt(trade.CreatedTimestamp, 10)).toISOString()),
        y: aevoBuys.map((trade) => parseFloat(trade.Price)),
        type: "scatter",
        mode: "markers",
        marker: {
          color: "green",
          symbol: "triangle-up",
          line: { width: 0 },
          size: aevoBuys.map((trade) => normalizeSize(parseFloat(trade.Amount), minAmount, maxAmount)),
        },
        text: aevoBuys.map(
          (trade) => `Price: ${trade.Price}, Amount: ${
              trade.Amount
            }, Timestamp: ${new Date(
              parseInt(trade.CreatedTimestamp, 10)
            ).toISOString()}`
        ),
        hoverinfo: "text",
        name: "AEVO Buys",
        showlegend: false,
      },
      {
        x: aevoSells.map((trade) => new Date(parseInt(trade.CreatedTimestamp, 10)).toISOString()),
        y: aevoSells.map((trade) => parseFloat(trade.Price)),
        type: "scatter",
        mode: "markers",
        marker: {
          color: "red",
          symbol: "triangle-down",
          line: { width: 0 },
          size: aevoSells.map((trade) => normalizeSize(parseFloat(trade.Amount), minAmount, maxAmount)),
        },
        text: aevoSells.map(
          (trade) => `Price: ${trade.Price}, Amount: ${
              trade.Amount
            }, Timestamp: ${new Date(
              parseInt(trade.CreatedTimestamp, 10)
            ).toISOString()}`
        ),
        hoverinfo: "text",
        name: "AEVO Sells",
        showlegend: false,
      },
      {
        x: secondaryExchangeBuys.map((trade) => new Date(parseInt(trade.CreatedTimestamp, 10)).toISOString()),
        y: secondaryExchangeBuys.map((trade) => parseFloat(trade.Price)),
        type: "scatter",
        mode: "markers",
        marker: {
          color: "turquoise",
          symbol: "triangle-up",
          line: { width: 0 },
          size: secondaryExchangeBuys.map((trade) => normalizeSize(parseFloat(trade.Amount), minAmount, maxAmount)),
        },
        text: secondaryExchangeBuys.map(
          (trade) => `Price: ${trade.Price}, Amount: ${
              trade.Amount
            }, Timestamp: ${new Date(
              parseInt(trade.CreatedTimestamp, 10)
            ).toISOString()}`
        ),
        hoverinfo: "text",
        name: `${secondaryExchange} Buys`,
        showlegend: false,
      },
      {
        x: secondaryExchangeSells.map((trade) => new Date(parseInt(trade.CreatedTimestamp, 10)).toISOString()),
        y: secondaryExchangeSells.map((trade) => parseFloat(trade.Price)),
        type: "scatter",
        mode: "markers",
        marker: {
          color: "yellow",
          symbol: "triangle-down",
          line: { width: 0 },
          size: secondaryExchangeSells.map((trade) => normalizeSize(parseFloat(trade.Amount), minAmount, maxAmount)),
        },
        text: secondaryExchangeSells.map(
          (trade) => `Price: ${trade.Price}, Amount: ${
              trade.Amount
            }, Timestamp: ${new Date(
              parseInt(trade.CreatedTimestamp, 10)
            ).toISOString()}`
        ),
        hoverinfo: "text",
        name: `${secondaryExchange} Sells`,
        showlegend: false,
      },
      {
        x: [null], // Invisible in the plot
        y: [null],
        type: "scatter",
        mode: "markers",
        marker: {
          color: "green",
          symbol: "triangle-up",
          size: legendMarkerSize,
        },
        name: "AEVO Buys",
        showlegend: true,
        hoverinfo: "none", // No hover info for this series
      },
      {
        x: [null],
        y: [null],
        type: "scatter",
        mode: "markers",
        marker: {
          color: "red",
          symbol: "triangle-down",
          size: legendMarkerSize,
        },
        name: "AEVO Sells",
        showlegend: true,
        hoverinfo: "none",
      },
      {
        x: [null], // Invisible in the plot
        y: [null],
        type: "scatter",
        mode: "markers",
        marker: {
          color: "turquoise",
          symbol: "triangle-up",
          size: legendMarkerSize,
        },
        name: `${secondaryExchange} Buys`,
        showlegend: true,
        hoverinfo: "none", // No hover info for this series
      },
      {
        x: [null],
        y: [null],
        type: "scatter",
        mode: "markers",
        marker: {
          color: "yellow",
          symbol: "triangle-down",
          size: legendMarkerSize,
        },
        name: `${secondaryExchange} Sells`,
        showlegend: true,
        hoverinfo: "none",
      },
    ] as Data[];
  }, [
    aevoTrades,
    markHistory,
    normalizeSize,
    secondaryExchange,
    secondaryExchangeTrades,
  ]);

  return (
    <ChartContainer>
      {!aevoTrades || !secondaryExchangeTrades || !markHistory ? (
        <div>... Loading {instrumentName}</div>
      ) : (
        <Plot
          data={plotData}
          layout={layout}
          useResizeHandler
          style={{ width: "calc(100% - 64px)", height: "calc(100% - 64px)" }}
        />
      )}
    </ChartContainer>
  );
}

export default InstrumentChart;
